import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_OPTIONS = [
  'Researcher',
  'Graduate Student',
  'PhD Candidate',
  'Postdoctoral Researcher',
  'Assistant Professor',
  'Associate Professor',
  'Full Professor',
  'Research Scientist',
  'Industry Researcher',
  'Independent Researcher',
  'Other',
];

interface ProfileCompletionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
  onSkip?: () => void;
}

const ProfileCompletionModal: React.FC<ProfileCompletionModalProps> = ({
  open,
  onOpenChange,
  onComplete,
  onSkip,
}) => {
  const { user } = useAuth();
  const [institution, setInstitution] = useState('');
  const [role, setRole] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!institution.trim()) {
      toast.error('Please enter your institution');
      return;
    }

    const finalRole = role === 'Other' ? customRole.trim() : role;
    if (!finalRole) {
      toast.error('Please specify your role');
      return;
    }

    if (!user) {
      toast.error('You must be logged in');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('users')
        .update({
          institution: institution.trim(),
          role: finalRole,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) {
        throw error;
      }

      toast.success('Profile updated successfully');
      onComplete();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onSkip?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl text-center">
            Complete Your Profile
          </DialogTitle>
          <DialogDescription className="text-center">
            Help other researchers find and connect with you
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="profile-institution">Institution</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="profile-institution"
                placeholder="Your university or organization"
                value={institution}
                onChange={(e) => setInstitution(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-role">Role</Label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
              <Select 
                value={role} 
                onValueChange={(val) => {
                  setRole(val);
                  if (val !== 'Other') setCustomRole('');
                }}
              >
                <SelectTrigger id="profile-role" className="pl-10">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {role === 'Other' && (
              <div className="mt-2">
                <Input
                  id="custom-role"
                  placeholder="e.g., Lab Manager, Research Assistant"
                  value={customRole}
                  onChange={(e) => setCustomRole(e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 pt-4">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Profile'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleSkip}
              className="text-muted-foreground"
            >
              Skip for now
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileCompletionModal;
